import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useCopyEvent, useCreateEvent, useEvents, useSeasons } from '@/api/queries';
import { Button } from '@/components/kit/Button';
import { ChoiceGroup, Option } from '@/components/kit/Controls';
import { DateField } from '@/components/kit/DatePicker';
import { Col, Form, Row, TextField } from '@/components/kit/Field';
import { SelectField } from '@/components/kit/Popover';
import { Breadcrumbs, Columns, PageHeader, SectionLabel } from '@/components/kit/Layout';
import { EmptyState, Note } from '@/components/kit/Note';
import { Panel, Stack } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';
import { dateRange } from '@/lib/format';

/**
 * Создание мероприятия: с нуля или копированием.
 *
 * Копируется всё, кроме афиши и положения — они всегда свои.
 * Дедлайны и даты повышения цены сдвигаются вместе с датой события,
 * сохраняя смещение: срок, стоявший за 17 дней до старта, останется
 * за 17 дней (БЛ §5.7).
 */
export function NewEventPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const seasons = useSeasons();
  const allEvents = useEvents();
  const create = useCreateEvent();
  const copy = useCopyEvent();

  const [mode, setMode] = useState<'blank' | 'copy'>('blank');
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const [city, setCity] = useState('');
  const [venue, setVenue] = useState('');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);

  // Копировать можно опубликованное или завершённое: черновик
  // ещё правится, и копия с него была бы копией намерения
  const sources = (allEvents.data ?? []).filter((e) => e.state !== 'draft');
  const source = sources.find((e) => e.id === sourceId);

  const season = seasonId ?? seasons.data?.[0]?.id ?? null;
  const canSubmit =
    mode === 'copy'
      ? Boolean(sourceId && dateFrom)
      : Boolean(season && city.trim() && dateFrom && dateTo);

  const fail = (e: Error) => toast.showError(e instanceof ApiError ? e.message : 'Не создалось');

  const submit = () => {
    if (mode === 'copy' && sourceId && dateFrom) {
      copy.mutate(
        {
          sourceId,
          dateFrom,
          dateTo: dateTo ?? undefined,
          seasonId: season ?? undefined,
          city: city.trim() || undefined,
        },
        {
          onSuccess: (created) => {
            toast.show(`Копия создана черновиком — афишу и положение загрузите заново`);
            navigate(`/events/${created.id}/main`);
          },
          onError: fail,
        },
      );
      return;
    }

    create.mutate(
      {
        seasonId: season,
        city: city.trim(),
        venue: venue.trim() || undefined,
        dateFrom,
        dateTo,
      },
      {
        onSuccess: (created) => {
          toast.show(`«${created.city}» создано черновиком`);
          navigate(`/events/${created.id}/main`);
        },
        onError: fail,
      },
    );
  };

  return (
    <div className="main">
      <Breadcrumbs
        items={[{ label: 'Мероприятия', onClick: () => navigate('/events') }, { label: 'Новое' }]}
      />

      <PageHeader title="Новое мероприятие">
        <Button tone="primary" onClick={submit} disabled={!canSubmit || create.isPending || copy.isPending}>
          Создать черновик
        </Button>
      </PageHeader>

      <Columns form>
        <Stack>
          <Panel title="Как создаём">
            <div className="field">
              <ChoiceGroup
                value={mode}
                onChange={setMode}
                items={[
                  { value: 'blank', label: 'С нуля' },
                  { value: 'copy', label: 'Копированием' },
                ]}
              />
            </div>
            {mode === 'copy' ? (
              <>
                <SectionLabel>Что копируем</SectionLabel>
                {sources.length ? (
                  sources.map((e) => (
                    <Option
                      key={e.id}
                      radio
                      checked={sourceId === e.id}
                      onToggle={() => {
                        setSourceId(e.id);
                        if (!city) setCity(`${e.city} (копия)`);
                      }}
                      title={`${e.city} · ${e.seasonName}`}
                      description={`${dateRange(e.dateFrom, e.dateTo)}${e.venue ? ` · ${e.venue}` : ''}`}
                    />
                  ))
                ) : (
                  <EmptyState>
                    Копировать пока нечего: нужно опубликованное или завершённое мероприятие
                  </EmptyState>
                )}
                <Note>
                  Копируется всё: номинации, услуги, программа блоков, пакеты с составом,
                  браслеты, дедлайны и план. <b>Афиша и положение не копируются</b> — они всегда свои
                </Note>
              </>
            ) : null}
          </Panel>

          <Panel title="Основное">
            <Form>
              <SelectField
                label="Сезон"
                value={season}
                onChange={setSeasonId}
                placeholder="выберите сезон"
                options={(seasons.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              />

              <TextField
                label="Город"
                value={city}
                onValue={setCity}
                placeholder={mode === 'copy' ? source?.city ?? 'Иркутск' : 'Иркутск'}
              />

              {mode === 'blank' ? (
                <TextField label="Площадка" value={venue} onValue={setVenue} />
              ) : null}

              <Row>
                <Col>
                  <DateField label="Дата начала" value={dateFrom} onChange={setDateFrom} />
                </Col>
                <Col>
                  <DateField
                    label="Дата окончания"
                    value={dateTo}
                    onChange={setDateTo}
                    alignRight
                    placeholder={mode === 'copy' ? 'как в источнике' : 'выбрать дату'}
                  />
                </Col>
              </Row>
            </Form>
          </Panel>
        </Stack>

        <Panel title="Что будет дальше">
          <Note>
            Мероприятие создаётся <b>черновиком</b>: его видно только внутри, продажи по нему
            не работают, заявки не принимаются
          </Note>
          <Note>
            Опубликовать получится, когда пройдут все двенадцать проверок — их список будет
            на вкладке «Основное»
          </Note>
          {mode === 'copy' && source && dateFrom ? (
            <Note>
              Дедлайны и даты повышения цены сдвинутся на разницу между {dateRange(source.dateFrom, null)}{' '}
              и {dateRange(dateFrom, null)}, сохраняя смещение
            </Note>
          ) : null}
        </Panel>
      </Columns>
    </div>
  );
}
